import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const DonutChart = ({ data, formatCurrency, height = 500, categories = [], focusedCategory }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoveredData, setHoveredData] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);

  // ResizeObserver to update width dynamically
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    
    handleResize(); // set initial width

    const resizeObserver = new window.ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  // Build a color map from categories
  const colorMap = {};
  categories.forEach(cat => {
    if (cat.name) colorMap[cat.name] = cat.color;
    if (cat.id) colorMap[cat.id] = cat.color;
  });

  // Fallback colors
  const fallbackColors = {
    income: '#10B981',
    expenses: '#EF4444',
    savings: '#3B82F6',
    general: '#6366F1',
    other: '#A3A3A3',
    netBalance: '#3B82F6',
  };

  // Generate consistent colors for items without predefined colors
  const generateColor = (index, category) => {
    const hues = [210, 340, 25, 120, 280, 45, 180, 300, 60, 240, 160, 320];
    return `hsl(${hues[index % hues.length]}, 65%, ${category === 'income' ? '55%' : '50%'})`;
  };

  // Process data for combined donut chart
  const { incomeData, expenseData, centerStats } = useMemo(() => {
    if (!data || !data.nodes || !data.links) {
      return { incomeData: [], expenseData: [], centerStats: { income: 0, expenses: 0, balance: 0 } };
    }

    // Filter income and expense nodes (excluding Total Income and Net Balance)
    const incomeNodes = data.nodes.filter(n => 
      n.category === 'income' && n.name !== 'Total Income'
    );
    const expenseNodes = data.nodes.filter(n => 
      n.category === 'expenses'
    );

    // Calculate totals
    const totalIncome = incomeNodes.reduce((sum, node) => sum + node.value, 0);
    const totalExpenses = expenseNodes.reduce((sum, node) => sum + node.value, 0);
    const balance = totalIncome - totalExpenses;

    // Prepare data for pie charts with colors
    const processNodes = (nodes, category) => nodes
      .sort((a, b) => b.value - a.value)
      .map((node, index) => {
        let color = colorMap[node.name] || fallbackColors[node.category] || generateColor(index, category);
        
        // Special color handling
        if (node.name === 'Other') color = fallbackColors.other;
        if (node.name === 'Net Balance') color = fallbackColors.netBalance;
        
        return {
          name: node.name,
          value: node.value,
          color,
          category,
          percentage: category === 'income' 
            ? (totalIncome > 0 ? (node.value / totalIncome) * 100 : 0)
            : (totalExpenses > 0 ? (node.value / totalExpenses) * 100 : 0)
        };
      });

    return {
      incomeData: processNodes(incomeNodes, 'income'),
      expenseData: processNodes(expenseNodes, 'expenses'),
      centerStats: {
        income: totalIncome,
        expenses: totalExpenses,
        balance
      }
    };
  }, [data, colorMap]);

  // Custom tooltip - removed as requested
  // No tooltip component needed

  // Center content component - responsive text sizing (further reduced)
  const CenterContent = ({ stats, hoveredData, selectedData, chartSize }) => {
    const dynamicFontSizes = {
      title: chartSize < 200 ? '0.65rem' : chartSize < 260 ? '0.8125rem' : '0.85rem',
      value: chartSize < 200 ? '0.95rem' : chartSize < 260 ? '1.1rem' : '1.3rem',
      subtitle: chartSize < 200 ? '0.55rem' : chartSize < 260 ? '0.65rem' : '0.75rem'
    };

    // Priority: selectedData > hoveredData > default
    const displayData = selectedData || hoveredData;

    if (displayData) {
      return (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          pointerEvents: 'none',
          zIndex: 10,
          maxWidth: chartSize * 0.6,
          px: 1,
          transition: 'all 0.3s ease'
        }}>
          <Typography sx={{ 
            fontWeight: 'bold', 
            color: 'text.primary',
            fontSize: dynamicFontSizes.title,
            mb: 0.5
          }}>
            {displayData.name}
          </Typography>
          <Typography sx={{ 
            fontWeight: 'bold',
            color: displayData.category === 'income' ? 'success.main' : 'error.main',
            fontSize: dynamicFontSizes.value,
            mb: 0.5
          }}>
            {formatCurrency(displayData.value)}
          </Typography>
          <Typography sx={{ 
            color: 'text.secondary',
            fontSize: dynamicFontSizes.subtitle
          }}>
            {displayData.percentage.toFixed(1)}% of {displayData.category === 'income' ? 'income' : 'expenses'}
          </Typography>
          {selectedData && (
            <Typography sx={{ 
              color: 'text.secondary',
              fontSize: dynamicFontSizes.subtitle,
              mt: 0.5,
              fontStyle: 'italic'
            }}>
              Click again to deselect
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        pointerEvents: 'none',
        zIndex: 10,
        maxWidth: chartSize * 0.6,
        px: 1,
        transition: 'all 0.3s ease'
      }}>
        <Typography sx={{ 
          fontWeight: 'bold', 
          color: 'success.main',
          fontSize: dynamicFontSizes.title,
          mb: 0.5
        }}>
          Income
        </Typography>
        <Typography sx={{ 
          fontWeight: 'bold',
          color: 'success.main',
          fontSize: dynamicFontSizes.value,
          mb: 1
        }}>
          {formatCurrency(stats.income)}
        </Typography>
        <Typography sx={{ 
          fontWeight: 'bold', 
          color: 'error.main',
          fontSize: dynamicFontSizes.title,
          mb: 0.5
        }}>
          Expenses
        </Typography>
        <Typography sx={{ 
          fontWeight: 'bold',
          color: 'error.main',
          fontSize: dynamicFontSizes.value
        }}>
          {formatCurrency(stats.expenses)}
        </Typography>
      </Box>
    );
  };

  // Calculate responsive sizes (reverted to previous; slightly large but not max)
  const maxChartSize = isMobile ? Math.min(height * 0.75, 360) : Math.min(height * 0.9, 460);
  const minChartSize = 200;
  const dynamicSize = containerWidth > 0 ? Math.min(containerWidth * (isMobile ? 0.95 : 0.7), maxChartSize) : maxChartSize;
  const chartSize = Math.max(minChartSize, dynamicSize);
  
  // Thinner rings with fixed thicknesses
  const OUTER_GAP = 20;          // padding to chart bounds
  const OUTER_THICKNESS = 28;    // +5 more thickness for the outer ring
  const RING_GAP = 12;           // gap between income and expenses rings
  const INNER_THICKNESS = 26;    // +5 more thickness for the inner ring

  const outerRadius = (chartSize / 2) - OUTER_GAP;
  const innerRadius = Math.max(outerRadius - OUTER_THICKNESS, 10);
  const expenseOuterRadius = Math.max(innerRadius - RING_GAP, 8);
  const expenseInnerRadius = Math.max(expenseOuterRadius - INNER_THICKNESS, 6);

  // Sync legend focus from parent to center selection
  useEffect(() => {
    if (focusedCategory) {
      const all = [...incomeData, ...expenseData];
      const match = all.find(d => d.name === focusedCategory);
      if (match) {
        setSelectedData(match);
        setHoveredData(null);
      }
    } else {
      setSelectedData(null);
    }
  }, [focusedCategory, incomeData, expenseData]);

  return (
  <Box 
      ref={containerRef}
      sx={{ 
        width: '100%', 
    height, 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
  p: 0.5
      }}
    >
      {(incomeData.length > 0 || expenseData.length > 0) ? (
        <>
          {/* Combined Donut Chart */}
           <Box 
             onMouseLeave={() => setHoveredData(null)}
             sx={{ 
            position: 'relative', 
            width: chartSize, 
            height: chartSize,
            mx: 'auto'
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                {/* Outer Ring - Income */}
                {incomeData.length > 0 && (
                  <Pie
                    data={incomeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={outerRadius}
                    innerRadius={innerRadius}
                    dataKey="value"
                    onMouseEnter={(data, index) => setHoveredData(data)}
                    onMouseLeave={() => setHoveredData(null)}
                    onClick={(data, index) => {
                      // Toggle selection
                      if (selectedData && selectedData.name === data.name) {
                        setSelectedData(null);
                      } else {
                        setSelectedData(data);
                      }
                    }}
                    animationBegin={0}
                    animationDuration={800}
                  >
                    {incomeData.map((entry, index) => (
                      <Cell 
                        key={`income-cell-${index}`} 
                        fill={entry.color}
                        stroke="white"
                        strokeWidth={2}
                        style={{
                          filter: (hoveredData && hoveredData.name === entry.name) || (selectedData && selectedData.name === entry.name) ? 'brightness(1.2)' : 'brightness(1)',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          transformOrigin: 'center',
                          transform: (hoveredData && hoveredData.name === entry.name) || (selectedData && selectedData.name === entry.name) ? 'scale(1.06)' : 'scale(1)'
                        }}
                      />
                    ))}
                  </Pie>
                )}

                {/* Inner Ring - Expenses */}
                {expenseData.length > 0 && (
                  <Pie
                    data={expenseData}
                    cx="50%"
                    cy="50%"
                    outerRadius={expenseOuterRadius}
                    innerRadius={expenseInnerRadius}
                    dataKey="value"
                    onMouseEnter={(data, index) => setHoveredData(data)}
                    onMouseLeave={() => setHoveredData(null)}
                    onClick={(data, index) => {
                      // Toggle selection
                      if (selectedData && selectedData.name === data.name) {
                        setSelectedData(null);
                      } else {
                        setSelectedData(data);
                      }
                    }}
                    animationBegin={400}
                    animationDuration={800}
                  >
                    {expenseData.map((entry, index) => (
                      <Cell 
                        key={`expense-cell-${index}`} 
                        fill={entry.color}
                        stroke="white"
                        strokeWidth={2}
                        style={{
                          filter: (hoveredData && hoveredData.name === entry.name) || (selectedData && selectedData.name === entry.name) ? 'brightness(1.2)' : 'brightness(1)',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          transformOrigin: 'center',
                          transform: (hoveredData && hoveredData.name === entry.name) || (selectedData && selectedData.name === entry.name) ? 'scale(1.08)' : 'scale(1)'
                        }}
                      />
                    ))}
                  </Pie>
                )}

              </PieChart>
            </ResponsiveContainer>

            {/* Center Content */}
            <CenterContent 
              stats={centerStats}
              hoveredData={hoveredData}
              selectedData={selectedData}
              chartSize={chartSize}
            />
          </Box>
        </>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          width: '100%',
          minHeight: 300
        }}>
          <Typography color="text.secondary" variant="h6">
            No data available to display
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DonutChart;
